/**
 * Tiny self-contained chart primitives (canvas 2D + DOM bars). No external
 * chart library / CDN — so the dashboard works under a strict CSP unchanged.
 */

const AXIS = "#2a3444";
const GRID = "#1c2431";

interface CanvasFit {
  ctx: CanvasRenderingContext2D;
  w: number;
  h: number;
}

/** Size a canvas to its CSS box at device-pixel resolution and clear it. */
export function fitCanvas(canvas: HTMLCanvasElement): CanvasFit {
  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();
  const w = Math.max(1, Math.round(rect.width));
  const h = Math.max(1, Math.round(rect.height));
  canvas.width = w * dpr;
  canvas.height = h * dpr;
  const ctx = canvas.getContext("2d")!;
  ctx.scale(dpr, dpr);
  ctx.clearRect(0, 0, w, h);
  return { ctx, w, h };
}

export interface LineOptions {
  color: string;
  fill?: string;
  yMin?: number;
  yMax?: number;
  /** Format a y value for the top-right readout. */
  format?: (v: number) => string;
}

/** Draw a filled line chart of `values` (oldest→newest) across the canvas. */
export function lineChart(canvas: HTMLCanvasElement, values: number[], opts: LineOptions): void {
  const { ctx, w, h } = fitCanvas(canvas);
  const pad = 4;
  const finite = values.filter((v) => Number.isFinite(v));
  const yMin = opts.yMin ?? (finite.length ? Math.min(...finite) : 0);
  const yMax = opts.yMax ?? (finite.length ? Math.max(...finite, yMin + 1) : 1);
  const range = yMax - yMin || 1;

  // baseline
  ctx.strokeStyle = AXIS;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(pad, h - pad);
  ctx.lineTo(w - pad, h - pad);
  ctx.stroke();

  if (values.length < 2) return;
  const x = (i: number) => pad + (i / (values.length - 1)) * (w - 2 * pad);
  const y = (v: number) => h - pad - ((clamp(v, yMin, yMax) - yMin) / range) * (h - 2 * pad);

  ctx.beginPath();
  values.forEach((v, i) => (i === 0 ? ctx.moveTo(x(i), y(v)) : ctx.lineTo(x(i), y(v))));
  ctx.strokeStyle = opts.color;
  ctx.lineWidth = 2;
  ctx.stroke();

  if (opts.fill) {
    ctx.lineTo(x(values.length - 1), h - pad);
    ctx.lineTo(x(0), h - pad);
    ctx.closePath();
    ctx.fillStyle = opts.fill;
    ctx.fill();
  }

  const last = values[values.length - 1]!;
  if (opts.format && Number.isFinite(last)) {
    ctx.fillStyle = opts.color;
    ctx.font = "11px ui-monospace, monospace";
    ctx.textAlign = "right";
    ctx.fillText(opts.format(last), w - pad, 12);
  }
}

/** Minimal sparkline (no fill, no axis) for compact trends. */
export function sparkline(canvas: HTMLCanvasElement, values: number[], color: string): void {
  const { ctx, w, h } = fitCanvas(canvas);
  const finite = values.filter((v) => Number.isFinite(v));
  if (finite.length < 2) return;
  const min = Math.min(...finite);
  const max = Math.max(...finite, min + 1);
  const x = (i: number) => (i / (values.length - 1)) * w;
  const y = (v: number) => h - ((v - min) / (max - min || 1)) * h;
  ctx.beginPath();
  values.forEach((v, i) => (i === 0 ? ctx.moveTo(x(i), y(v)) : ctx.lineTo(x(i), y(v))));
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  ctx.stroke();
}

export interface Bar {
  label: string;
  value: number;
  color: string;
  /** Text shown on the right (defaults to the value). */
  text?: string;
}

/** Render horizontal bars into a container as DOM (crisp at any zoom). */
export function domBars(el: HTMLElement, bars: Bar[], unit = ""): void {
  const max = Math.max(1, ...bars.map((b) => (Number.isFinite(b.value) ? b.value : 0)));
  el.innerHTML = "";
  for (const b of bars) {
    const v = Number.isFinite(b.value) ? b.value : 0;
    const row = document.createElement("div");
    row.className = "bar-row";
    const label = document.createElement("span");
    label.className = "bar-label";
    label.textContent = b.label;
    const track = document.createElement("div");
    track.className = "bar-track";
    const fill = document.createElement("div");
    fill.className = "bar-fill";
    fill.style.width = `${(v / max) * 100}%`;
    fill.style.background = b.color;
    track.appendChild(fill);
    const val = document.createElement("span");
    val.className = "bar-val";
    val.textContent = b.text ?? `${round(v)}${unit}`;
    row.append(label, track, val);
    el.appendChild(row);
  }
}

/** Render a single stacked bar (source mix) as proportional DOM segments. */
export function domStacked(el: HTMLElement, segments: Bar[]): void {
  const total = segments.reduce((a, s) => a + (s.value || 0), 0) || 1;
  el.innerHTML = "";
  const bar = document.createElement("div");
  bar.className = "stacked";
  for (const s of segments) {
    if (!s.value) continue;
    const seg = document.createElement("div");
    seg.style.width = `${(s.value / total) * 100}%`;
    seg.style.background = s.color;
    seg.title = `${s.label}: ${s.text ?? round(s.value)}`;
    bar.appendChild(seg);
  }
  el.appendChild(bar);
  const legend = document.createElement("div");
  legend.className = "legend";
  for (const s of segments) {
    const item = document.createElement("span");
    const swatch = document.createElement("i");
    swatch.style.background = s.color;
    item.append(swatch, document.createTextNode(` ${s.label} ${s.text ?? ""}`));
    legend.appendChild(item);
  }
  el.appendChild(legend);
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}
function round(n: number): number {
  return Math.round(n * 10) / 10;
}

export { GRID };
